import React, { useEffect, useState } from 'react'
import Dropdown from 'components/generic/Dropdown'
import { GameZoneBounds, GameZones, RatingFilter } from 'types/global'
import styles from './styles/dropdownRatingsCombined.module.scss'
import CombinedRatingFilter from './CombinedRatingFilter/CombinedRatingFilter'
import { useCombinedRatingFilter } from './CombinedRatingFilter/hooks'
import { transformGameZoneValues, transformRatingForLabel } from './CombinedRatingFilter/utils'
import { useTranslation } from 'react-i18next'
import { useGameZone } from 'hooks/use-gamezone'

interface Props {
  id: string
  minRange: number
  maxRange: number
  stepSize: number
  defaultLabel: string
  appliedRangeLabel: string
  rating: RatingFilter | null
  setRating: (rating: RatingFilter | null) => void
}

const DropdownRatingCombined = ({
  id,
  defaultLabel,
  appliedRangeLabel,
  minRange,
  maxRange,
  stepSize,
  rating,
  setRating,
}: Props) => {
  const { t } = useTranslation(['strCommonUI', 'strFilters'])
  const [isOpen, setIsOpen] = useState(false)
  const gameZone = useGameZone()
  const {
    rangeInputHandlers,
    reconcileInternalState,
    defaultInternalState,
    internalState,
    setInternalState,
  } = useCombinedRatingFilter({ rating, minRange, maxRange })
  const appliedRange = rating && (rating?.gameZone ?? rating.range)
  const dropdownLabel = !appliedRange
    ? defaultLabel
    : t('rating.compositeDropdownLabel', {
        ns: 'strFilters',
        appliedLabel: appliedRangeLabel,
        min: transformRatingForLabel(appliedRange.max),
        max: transformRatingForLabel(appliedRange.min),
      })

  const handleCancel = () => {
    setIsOpen(false)
    reconcileInternalState()
  }

  const handleConfirm = () => {
    if (internalState.canApply && !internalState.error) {
      const { type, confidence, range, isGameZoneApplied } = internalState
      const gameZoneValues =
        gameZone?.[type] && transformGameZoneValues(gameZone[type] as GameZoneBounds)

      setRating({
        type,
        confidence,
        range,
        gameZone: isGameZoneApplied && gameZoneValues ? gameZoneValues : undefined,
      })
    } else {
      setRating(null)
      setInternalState(defaultInternalState)
    }
    setIsOpen(false)
  }

  const handleOpenDropdownChange = (open: boolean) => {
    if (open) reconcileInternalState()
  }

  useEffect(() => reconcileInternalState, [reconcileInternalState])

  return (
    <Dropdown
      contentTrigger={dropdownLabel}
      controls={{ isOpen, setIsOpen }}
      withChevron
      cancelBtn={{
        content: t('button.cancel', { ns: 'strCommonUI' }),
        onClick: handleCancel,
      }}
      confirmBtn={{
        content: t('button.applyFilter', { ns: 'strCommonUI' }),
        onClick: handleConfirm,
        disabled: !!internalState.error,
      }}
      classNames={{
        contentWrapper: styles.contentWrapper,
        content: styles.content,
        trigger: { '--is-active': !!rating },
        confirmBtn: styles.confirmBtn,
      }}
      onBeforeOpenChange={handleOpenDropdownChange}
    >
      <CombinedRatingFilter
        id={id}
        minRange={minRange}
        maxRange={maxRange}
        stepSize={stepSize}
        internalState={internalState}
        setInternalState={setInternalState}
        gameZone={gameZone}
        {...rangeInputHandlers}
      />
    </Dropdown>
  )
}

export default DropdownRatingCombined
